import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { adminLoginHistory } from "@/lib/api";
import { Empty, TH, TR, errMsg } from "@/components/accounts/shared";

// Staff sign-ins (Anish, 23 Oct 2026: keep the login history YoActiv had). Every Google sign-in is
// logged with its IP and browser from 23 Oct; before that, only sessions still open are known.
const SEL = "bg-[#0a0a0a] border border-[#262626] rounded-md h-8 px-2 text-xs text-zinc-200";

const device = (ua) => {
  if (!ua) return "";
  const os = /iPhone|iPad/.test(ua) ? "iPhone" : /Android/.test(ua) ? "Android" : /Mac OS/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : "";
  const br = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : /Firefox\//.test(ua) ? "Firefox" : "";
  return [br, os].filter(Boolean).join(" on ");
};

export default function LoginHistory() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [gym, setGym] = useState("");
  const [who, setWho] = useState("");
  const load = useCallback(async () => {
    setData(null);
    try { setData(await adminLoginHistory(days)); } catch (e) { toast.error(errMsg(e, "Could not load")); setData({ logins: [] }); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const gyms = useMemo(() => [...new Set((data?.logins || []).flatMap((r) => r.gyms || []))].sort(), [data]);
  const people = useMemo(() => [...new Map((data?.logins || []).map((r) => [r.user_id, r.name])).entries()]
    .sort((a, b) => String(a[1]).localeCompare(String(b[1]))), [data]);

  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;
  const shown = data.logins.filter((r) => (!gym || (r.gyms || []).includes(gym)) && (!who || r.user_id === who));
  return (
    <div className="card-surface overflow-x-auto" data-testid="login-history">
      <div className="px-4 py-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-zinc-100">Logins · {shown.length}</div>
          <div className="text-[11px] text-zinc-500 max-w-2xl">Every staff sign-in to FPA, newest first, with the gyms they work at. IP and device are recorded from 23 Oct 2026; earlier rows are sign-ins whose session is still open.</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className={SEL} data-testid="login-history-days">
            <option value={1}>Today</option><option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option>
          </select>
          <select value={gym} onChange={(e) => setGym(e.target.value)} className={SEL} data-testid="login-history-gym">
            <option value="">All gyms</option>
            {gyms.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <select value={who} onChange={(e) => setWho(e.target.value)} className={SEL} data-testid="login-history-person">
            <option value="">Everyone</option>
            {people.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        </div>
      </div>
      {shown.length === 0 ? <Empty>No sign-ins in this period.</Empty> : (
        <table className="w-full text-sm min-w-[760px]">
          <thead><tr className={TH}>
            <th className="px-3 py-2 font-medium">When</th><th className="px-3 py-2 font-medium">Who</th>
            <th className="px-3 py-2 font-medium">Gyms</th><th className="px-3 py-2 font-medium">Device</th><th className="px-3 py-2 font-medium">IP</th>
          </tr></thead>
          <tbody>
            {shown.map((r, i) => (
              <tr key={`${r.user_id}-${r.at}-${i}`} className={TR}>
                <td className="px-3 py-2 text-xs text-zinc-300 whitespace-nowrap">{new Date(r.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</td>
                <td className="px-3 py-2 text-xs"><div className="text-zinc-200">{r.name}</div><div className="text-[10px] text-zinc-500">{r.email}</div></td>
                <td className="px-3 py-2 text-xs text-zinc-400">{(r.gyms || []).join(", ") || "—"}</td>
                <td className="px-3 py-2 text-xs text-zinc-400">{device(r.device) || (r.source === "session" ? "Not recorded" : "—")}</td>
                <td className="px-3 py-2 text-xs text-zinc-500 font-mono">{r.ip || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
